import {TooltipNode} from './model/tooltip-node.model';
import {fromEvent, switchMapTo, Observable, take, skip, tap, repeat, mapTo, filter, merge} from 'rxjs';
import {TooltipTemplate} from './tooltip.template';
import {createPopper, flip, preventOverflow, Instance as Popper} from '@popperjs/core';
import {Tooltip} from './tooltip.directive';

export class TooltipDomInjector {
    template: TooltipTemplate;
    popper: Popper;

    private focus: Observable<Event> = fromEvent(this.hostElement, 'focus');
    private blur: Observable<Event> = fromEvent(this.hostElement, 'blur');
    private mouseover: Observable<Event> = fromEvent(this.hostElement, 'mouseover');
    private mouseout: Observable<Event> = fromEvent(this.hostElement, 'mouseout');

    private openStream: Observable<Event> = merge(
        this.focus,
        this.mouseover
    ).pipe(
        tap(() => this.tooltip.openStream.next(this.tooltipNode))
    );

    private closeStream: Observable<Event> = merge(
        this.blur,
        this.mouseout,
        this.tooltip.openStream.pipe(
            filter((tooltipNode: TooltipNode) => tooltipNode !== this.tooltipNode),
            mapTo(null)
        )
    );

    private controls: Observable<Event> = this.openStream.pipe(
        take(1),
        tap(() => {
            this.appendTooltipView();
        }),
        switchMapTo(this.closeStream),
        take(1),
        tap(() => {
            this.removeTooltipView();
        }),
        repeat()
    );

    constructor(
        private document: Document,
        private hostElement: HTMLElement,
        private tooltip: Tooltip,
        private tooltipNode: TooltipNode,
    ) {
        this.makeFocusable();

        this.tooltipNode.initializer.pipe(
            take(1),
            tap(() => this.initTooltipView()),
            switchMapTo(this.controls)
        ).subscribe();

        this.tooltipNode.initializer.pipe(
            skip(1),
            tap(() => this.refreshTooltipView())
        ).subscribe();
    }

    private makeFocusable(): void {
        const tabindex = this.hostElement.getAttribute('tabindex');
        if (tabindex === null || tabindex === '-1') {
            this.hostElement.setAttribute('tabindex', '0');
        }
    }

    private initTooltipView(): void {
        this.template = new TooltipTemplate(this.document, this.tooltipNode)
    }

    private refreshTooltipView(): void {
        this.template.setContent();
    }

    private appendTooltipView(): void {
        this.hostElement.parentNode.insertBefore(this.template.container, this.hostElement.nextSibling);
        this.popper = createPopper(this.hostElement, this.template.container, {
            modifiers: [
                preventOverflow,
                flip
            ]
        });
    }

    private removeTooltipView(): void {
        this.hostElement.parentElement.removeChild(this.hostElement.nextSibling);
        this.popper.destroy();
    }
}

